import { useCallback, useMemo, useState } from 'react';

import {
	BUDGET,
	DEFAULT_SHAPE,
	POSITIONS,
	SQUAD_QUOTA,
	clubCounts,
	benchOrder,
	emptySquad,
	fillXiFirst,
	formationLabel,
	isComplete,
	isLegalShape,
	outstanding,
	shapeAfterSwap,
	splitSquad,
	syncBenchSequence,
	toPayload,
	totalCost,
} from './draftRules.js';

/*
 * Squad state.
 *
 * The squad is four arrays, one per position, each fixed at its quota
 * length. Position in the array *is* the pitch/bench distinction: the
 * first N of each are on the pitch, where N comes from `shape`.
 *
 * `shape` is not chosen from a list. It starts at 4-4-2 and moves on its
 * own two accounts: picking, which grows a line so the XI fills before the
 * bench does, and substituting, where bringing a defender on for a
 * midfielder is what makes it a 5-3-2. That keeps one gesture in the
 * interface instead of two that can disagree, and the bench composition
 * stays impossible to get wrong: given a shape and the fixed 2/5/5/3
 * quota, there is only one bench it can have.
 */

export function shapeFromDraft(draft) {
	const byElement = new Map(draft.picks.map((pick) => [pick.element, pick]));
	const shape = { GKP: 0, DEF: 0, MID: 0, FWD: 0 };
	for (const element of draft.xi) {
		const pick = byElement.get(element);
		if (pick && shape[pick.position] !== undefined) shape[pick.position] += 1;
	}
	return isLegalShape(shape) ? shape : { ...DEFAULT_SHAPE };
}

export function squadFromDraft(draft) {
	const squad = emptySquad();
	const byElement = new Map(draft.picks.map((pick) => [pick.element, pick]));
	const buckets = { GKP: [], DEF: [], MID: [], FWD: [] };

	/* Pitch before bench, so array order carries the split. */
	for (const element of [...draft.xi, ...draft.bench]) {
		const pick = byElement.get(element);
		if (pick && buckets[pick.position]) buckets[pick.position].push(pick);
	}
	for (const position of POSITIONS) {
		buckets[position].slice(0, SQUAD_QUOTA[position]).forEach((pick, index) => {
			squad[position][index] = pick;
		});
	}
	return squad;
}

export function useDraftSquad(initial) {
	const [name, setName] = useState(initial?.name || '');
	const [state, setState] = useState(() => {
		const squad = initial?.draft ? squadFromDraft(initial.draft) : emptySquad();
		const shape = initial?.draft ? shapeFromDraft(initial.draft) : { ...DEFAULT_SHAPE };
		/* A loaded draft already carries an order; draft.bench is stored in
		 * priority order, so it seeds the sequence directly. */
		return { squad, shape, benchSeq: syncBenchSequence(squad, shape, initial?.draft?.bench) };
	});
	const [dirty, setDirty] = useState(false);

	const { squad, shape, benchSeq } = state;

	const place = useCallback((position, index, player) => {
		setState((prev) => {
			const line = [...prev.squad[position]];
			line[index] = player;
			const squad = { ...prev.squad, [position]: line };
			/* Reshape so the pick lands in the XI if there is room for it —
			 * a third forward should start, not sit behind four empty
			 * defender slots. */
			const shape = fillXiFirst(squad, prev.shape);
			return { squad, shape, benchSeq: syncBenchSequence(squad, shape, prev.benchSeq) };
		});
		setDirty(true);
	}, []);

	const remove = useCallback((position, index) => {
		setState((prev) => {
			const line = [...prev.squad[position]];
			line[index] = null;
			const squad = { ...prev.squad, [position]: line };
			return { ...prev, squad, benchSeq: syncBenchSequence(squad, prev.shape, prev.benchSeq) };
		});
		setDirty(true);
	}, []);

	/*
	 * Swap two players.
	 *
	 * Within a position it is a straight exchange of array slots, which
	 * covers both a substitution and simply reordering a row — the same
	 * operation, and the rules cannot tell them apart.
	 *
	 * Across positions the shape moves with them, which is what replaces
	 * the formation control, and it is refused if the result would not be
	 * a legal XI so a third centre-back cannot come on for the only
	 * striker.
	 */
	const substitute = useCallback((a, b) => {
		setState((prev) => {
			if (a.position === b.position) {
				const line = [...prev.squad[a.position]];
				[line[a.index], line[b.index]] = [line[b.index], line[a.index]];
				const squad = { ...prev.squad, [a.position]: line };
				return {
					...prev,
					squad,
					benchSeq: syncBenchSequence(squad, prev.shape, prev.benchSeq),
				};
			}

			const aStarting = a.index < prev.shape[a.position];
			const bStarting = b.index < prev.shape[b.position];
			/* Two starters of different positions would mean putting a
			 * midfielder in the defenders' row, which is not a thing. */
			if (aStarting === bStarting) return prev;

			const out = aStarting ? a : b;
			const into = aStarting ? b : a;
			const nextShape = shapeAfterSwap(prev.shape, out.position, into.position);
			if (!isLegalShape(nextShape)) return prev;

			/* Move the outgoing player to the last pitch slot of their
			 * position and the incoming one to the first bench slot of
			 * theirs; shrinking and growing the counts then lands both on
			 * the right side of the line. */
			const outLine = [...prev.squad[out.position]];
			const lastOnPitch = prev.shape[out.position] - 1;
			[outLine[out.index], outLine[lastOnPitch]] = [outLine[lastOnPitch], outLine[out.index]];

			const intoLine = [...prev.squad[into.position]];
			const firstOnBench = prev.shape[into.position];
			[intoLine[into.index], intoLine[firstOnBench]] = [
				intoLine[firstOnBench],
				intoLine[into.index],
			];

			const squad = { ...prev.squad, [out.position]: outLine, [into.position]: intoLine };
			return {
				squad,
				shape: nextShape,
				benchSeq: syncBenchSequence(squad, nextShape, prev.benchSeq),
			};
		});
		setDirty(true);
	}, []);

	/* Reorder the outfield substitutes. Nothing about the squad changes —
	 * only which of them comes on first. */
	const reorderBench = useCallback((from, to) => {
		setState((prev) => {
			const seq = syncBenchSequence(prev.squad, prev.shape, prev.benchSeq);
			if (from === to || !seq[from] || !seq[to]) return prev;
			const next = [...seq];
			[next[from], next[to]] = [next[to], next[from]];
			return { ...prev, benchSeq: next };
		});
		setDirty(true);
	}, []);

	/* Whether `candidate` is a legal partner for `selected` — drives which
	 * cards light up on the pitch. Covers three moves that look the same
	 * to the person doing them: substituting, reordering a row, and
	 * reordering the bench. */
	const canSwap = useCallback(
		(selected, candidate) => {
			if (!selected || !candidate) return false;
			if (!squad[candidate.position][candidate.index]) return false;
			if (selected.position === candidate.position && selected.index === candidate.index) {
				return false;
			}

			/* Two outfield substitutes: swapping them changes only the order
			 * they come on in, which is legal whatever positions they are. */
			if (selected.benchIndex != null && candidate.benchIndex != null) return true;

			const selectedStarting = selected.index < shape[selected.position];
			const candidateStarting = candidate.index < shape[candidate.position];

			/* Same position is always fine: on the pitch it reorders the row,
			 * across the line it is a substitution. */
			if (selected.position === candidate.position) return true;

			if (selectedStarting === candidateStarting) return false;
			const out = selectedStarting ? selected : candidate;
			const into = selectedStarting ? candidate : selected;
			return isLegalShape(shapeAfterSwap(shape, out.position, into.position));
		},
		[squad, shape],
	);

	const changeName = useCallback((next) => {
		setName(next);
		setDirty(true);
	}, []);

	/* Drops a whole squad in at once (the auto pick). The shape restarts
	 * from the default and is then grown to fit, so the result is the same
	 * as if the fifteen had been picked one by one. */
	const replaceSquad = useCallback((next) => {
		const shape = fillXiFirst(next, { ...DEFAULT_SHAPE });
		setState({ squad: next, shape, benchSeq: syncBenchSequence(next, shape, []) });
		setDirty(true);
	}, []);

	const clear = useCallback(() => {
		setState({ squad: emptySquad(), shape: { ...DEFAULT_SHAPE }, benchSeq: [] });
		setDirty(true);
	}, []);

	const derived = useMemo(() => {
		const spent = totalCost(squad);
		return {
			split: splitSquad(squad, shape),
			bench: benchOrder(squad, shape, benchSeq),
			formation: formationLabel(shape),
			spent,
			remaining: BUDGET - spent,
			clubs: clubCounts(squad),
			complete: isComplete(squad),
			problems: outstanding(squad),
			filled: POSITIONS.reduce(
				(count, position) => count + squad[position].filter(Boolean).length,
				0,
			),
		};
	}, [squad, shape, benchSeq]);

	return {
		name,
		squad,
		shape,
		dirty,
		...derived,
		setName: changeName,
		place,
		remove,
		replaceSquad,
		substitute,
		reorderBench,
		canSwap,
		clear,
		markSaved: () => setDirty(false),
		payload: () => toPayload(squad, shape, name, benchSeq),
	};
}
